import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PriceSiteLayout from "./PriceSiteLayout";
import PriceLiveMap from "./PriceLiveMap";
import useDeviceOS from "@/lib/useDeviceOS";

/**
 * Price — Homepage officielle du site pricecovoit.fr
 * Structure : Hero (avec CTA + réseaux sociaux) + Trust bar + Comment ça
 * marche + Vraies captures d'app + Pourquoi Price (chaud) + Témoignages +
 * FAQ + CTA.
 */

const steps = [
  { emoji: "🚗", title: "Choisis ton trajet", text: "Départ et arrivée, en quelques taps.", bg: "#FFE9D6" },
  { emoji: "🤝", title: "Trouve quelqu'un qui fait le même trajet", text: "En quelques secondes.", bg: "#B8E8D3" },
  { emoji: "💸", title: "Partage le trajet", text: "Simple. Rapide. Moins cher.", bg: "#F4A825" },
];

const perks = [
  { emoji: "⚡", title: "Covoiturage instantané", desc: "Trouve un covoitureur en quelques secondes, pas dans 3 jours." },
  { emoji: "🔒", title: "Paiement sécurisé", desc: "Cash ou carte, chaque paiement est encadré et protégé." },
  { emoji: "📍", title: "Partout en ville", desc: "Ton quartier, ton campus, ta rue — Price te suit." },
  { emoji: "💸", title: "Beaucoup moins cher", desc: "Le prix se divise. Ton budget mensuel te dit merci." },
];

const testimonials = [
  { text: "Enfin une vraie solution de covoiturage urbain.", author: "Léa · Paris", tint: "#FFE9D6" },
  { text: "Le concept est simple et très prometteur.", author: "Karim · Lyon", tint: "#B8E8D3" },
  { text: "J'ai hâte de pouvoir l'utiliser au quotidien.", author: "Manon · Marseille", tint: "#FDD7B0" },
];

const faq = [
  { q: "Est-ce un VTC ?", a: "Non. Price est une plateforme de covoiturage. Les covoitureurs partagent leur trajet, ils ne sont pas des chauffeurs professionnels." },
  { q: "Puis-je réserver immédiatement ?", a: "Oui. C'est même l'idée : tu publies ta demande, elle part instantanément aux covoitureurs proches, et tu es pris en charge en quelques minutes." },
  { q: "Comment sont calculés les prix ?", a: "Le prix dépend du trajet (distance, durée) et t'est affiché AVANT que tu confirmes la réservation. Pas de mauvaise surprise." },
  { q: "Puis-je payer en espèces ?", a: "Oui. Tu choisis entre paiement carte (sécurisé via Stripe) ou paiement cash au covoitureur au moment de la course." },
  { q: "Est-ce que je peux annuler ?", a: "Oui, tant que la course n'a pas démarré. L'annulation est gratuite si tu préviens à l'avance." },
];

function PhoneFrame({ src, alt, rotate = 0, delay = "0s" }) {
  return (
    <div
      className="animate-bounce-soft"
      style={{ transform: `rotate(${rotate}deg)`, animationDelay: delay }}
    >
      <div
        className="w-[220px] rounded-[38px] bg-white p-2 sm:w-[260px] sm:p-[10px]"
        style={{ border: "3px solid #1E0910", boxShadow: "8px 8px 0 0 #1E0910" }}
      >
        <img
          src={src}
          alt={alt}
          className="block h-auto w-full rounded-[30px]"
          loading="lazy"
        />
      </div>
    </div>
  );
}

export default function PriceHome() {
  const { os, hydrated } = useDeviceOS();
  const isAndroid = hydrated && os === "android";

  useEffect(() => {
    document.title = "Price — Le covoiturage instantané du quotidien";
    const setMeta = (name, content) => {
      let el = document.head.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Price est l'app de covoiturage instantané. Parce qu'un trajet coûte moins quand il se partage.");
    setMeta("keywords", "covoiturage, covoiturage instantané, Price, Price covoit, trajet partagé, covoiturage ville, covoiturage quotidien");
    let ldScript = document.getElementById("price-faq-jsonld");
    if (!ldScript) {
      ldScript = document.createElement("script");
      ldScript.type = "application/ld+json";
      ldScript.id = "price-faq-jsonld";
      document.head.appendChild(ldScript);
    }
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    return () => { if (ldScript) ldScript.remove(); };
  }, []);

  return (
    <PriceSiteLayout>
      {/* CARTE LIVE — en tête de page, c'est le wow-factor n°1 */}
      <PriceLiveMap />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(30,9,16,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(30,9,16,0.06) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
          aria-hidden
        />
        <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full blur-3xl" style={{ background: "rgba(248,106,120,0.20)" }} aria-hidden />
        <div className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full blur-3xl" style={{ background: "rgba(244,168,37,0.30)" }} aria-hidden />

        <div className="relative mx-auto grid max-w-6xl gap-10 px-5 py-14 md:grid-cols-2 md:items-center md:py-24">
          <div>
            <h1 className="mt-6 font-[Outfit] text-[44px] font-black leading-[0.95] tracking-tight sm:text-[56px] lg:text-[72px]" style={{ color: "#1E0910" }}>
              Le covoiturage{" "}
              <span className="relative inline-block">
                <span className="relative z-10" style={{ color: "#F86A78" }}>instantané</span>
                <span className="absolute inset-x-0 bottom-1 -z-0 h-4 rotate-[-1deg]" style={{ background: "rgba(248,106,120,0.25)" }} aria-hidden />
              </span>{" "}
              du quotidien.
            </h1>
            <p className="mt-5 max-w-lg font-[Manrope] text-lg" style={{ color: "rgba(30,9,16,0.7)" }}>
              Parce qu&apos;un trajet coûte moins quand il se partage.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              {isAndroid ? (
                <a
                  href="/welcome"
                  data-testid="hero-android-web-cta"
                  className="street-press group inline-flex items-center gap-3 rounded-full px-7 py-4 text-sm font-black uppercase tracking-widest text-white street-border"
                  style={{ background: "#F86A78", boxShadow: "8px 8px 0 0 #1E0910" }}
                >
                  <span>Ouvrir Price (version web)</span>
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-white street-border-thin group-hover:rotate-12 transition-transform" style={{ color: "#F86A78" }}>
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  </span>
                </a>
              ) : (
                <Link
                  to="/download"
                  data-testid="hero-download-cta"
                  className="street-press group inline-flex items-center gap-3 rounded-full px-7 py-4 text-sm font-black uppercase tracking-widest text-white street-border"
                  style={{ background: "#F86A78", boxShadow: "8px 8px 0 0 #1E0910" }}
                >
                  <span>Télécharger l&apos;application</span>
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-white street-border-thin group-hover:rotate-12 transition-transform" style={{ color: "#F86A78" }}>
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              )}
              <a
                href="#comment"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold street-border-thin hover:-translate-y-0.5 transition-transform"
                style={{ color: "#1E0910", boxShadow: "3px 3px 0 0 #1E0910" }}
              >
                Comment ça marche
              </a>
            </div>

            {/* Bandeau Android — Play Store publié : lien direct. */}
            {isAndroid && (
              <a
                href="https://play.google.com/store/apps/details?id=com.price.covoiturage"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-android-play-cta"
                className="mt-4 inline-flex items-start gap-2 rounded-2xl bg-white px-4 py-3 text-[13px] street-border-thin transition-transform hover:-translate-y-0.5"
                style={{ boxShadow: "3px 3px 0 0 #1E0910", maxWidth: 420 }}
              >
                <span className="text-xl leading-none">🤖</span>
                <span style={{ color: "rgba(30,9,16,0.8)" }}>
                  <b>Télécharge Price sur Google Play</b> — c&apos;est officiel !
                </span>
              </a>
            )}

            {/* Réseaux sociaux — remplace l'ancien compte à rebours de
                lancement. On garde le layout compact avec deux CTA côte
                à côte pour Instagram et TikTok. */}
            <div className="mt-8 rounded-2xl bg-white p-4 street-border-thin" style={{ boxShadow: "3px 3px 0 0 #1E0910", maxWidth: 420 }}>
              <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "rgba(30,9,16,0.6)" }}>
                Suis-nous sur les réseaux
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <a
                  href="https://www.instagram.com/pricecovoit"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-social-instagram"
                  className="group flex items-center gap-2 rounded-xl px-3 py-2.5 street-border-thin active:translate-y-[1px] transition-transform"
                  style={{
                    background: "linear-gradient(135deg,#F58529 0%,#DD2A7B 50%,#8134AF 100%)",
                    boxShadow: "3px 3px 0 0 #1E0910",
                  }}
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor" aria-hidden>
                    <path d="M12 2.2c3.2 0 3.6 0 4.85.07 1.17.05 1.8.25 2.22.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.05.42 2.22.06 1.26.07 1.64.07 4.85s0 3.6-.07 4.85c-.05 1.17-.25 1.8-.42 2.22-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.05.37-2.22.42-1.26.06-1.64.07-4.85.07s-3.6 0-4.85-.07c-1.17-.05-1.8-.25-2.22-.42-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.17-.42-.37-1.05-.42-2.22C2.2 15.6 2.2 15.2 2.2 12s0-3.6.07-4.85c.05-1.17.25-1.8.42-2.22.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.05-.37 2.22-.42C8.4 2.2 8.8 2.2 12 2.2Zm0 1.98c-3.15 0-3.52 0-4.76.07-1.07.05-1.65.23-2.04.38-.51.2-.87.43-1.26.82-.39.39-.62.75-.82 1.26-.15.39-.33.97-.38 2.04C2.65 8.48 2.65 8.85 2.65 12s0 3.52.07 4.76c.05 1.07.23 1.65.38 2.04.2.51.43.87.82 1.26.39.39.75.62 1.26.82.39.15.97.33 2.04.38 1.24.07 1.61.07 4.76.07s3.52 0 4.76-.07c1.07-.05 1.65-.23 2.04-.38.51-.2.87-.43 1.26-.82.39-.39.62-.75.82-1.26.15-.39.33-.97.38-2.04.07-1.24.07-1.61.07-4.76s0-3.52-.07-4.76c-.05-1.07-.23-1.65-.38-2.04-.2-.51-.43-.87-.82-1.26-.39-.39-.75-.62-1.26-.82-.39-.15-.97-.33-2.04-.38-1.24-.07-1.61-.07-4.76-.07Zm0 3.4a4.42 4.42 0 1 1 0 8.84 4.42 4.42 0 0 1 0-8.84Zm0 1.98a2.44 2.44 0 1 0 0 4.88 2.44 2.44 0 0 0 0-4.88Zm4.65-2.14a1.03 1.03 0 1 1 0 2.06 1.03 1.03 0 0 1 0-2.06Z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-black uppercase tracking-widest text-white/80">Instagram</div>
                    <div className="font-[Outfit] text-[13px] font-black text-white leading-none truncate">@pricecovoit</div>
                  </div>
                </a>
                <a
                  href="https://www.tiktok.com/@price.covoit"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-social-tiktok"
                  className="group flex items-center gap-2 rounded-xl px-3 py-2.5 street-border-thin active:translate-y-[1px] transition-transform"
                  style={{
                    background: "#000",
                    boxShadow: "3px 3px 0 0 #1E0910",
                  }}
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                    <path fill="#25F4EE" d="M19.6 6.9a5.6 5.6 0 0 1-4-1.5V15a5.7 5.7 0 1 1-5.6-5.7v3.2a2.5 2.5 0 1 0 2.4 2.5V2h3.2a5.6 5.6 0 0 0 4 3.7v1.2Z" />
                    <path fill="#FE2C55" d="M21 8.4a5.6 5.6 0 0 1-4-1.5V16.5a5.7 5.7 0 1 1-5.6-5.7v3.2a2.5 2.5 0 1 0 2.4 2.5V3.5h3.2a5.6 5.6 0 0 0 4 3.7v1.2Z" />
                    <path fill="#fff" d="M20.3 7.6a5.6 5.6 0 0 1-4-1.5v9.7A5.7 5.7 0 1 1 10.7 10v3.2a2.5 2.5 0 1 0 2.4 2.5V2.7h3.2a5.6 5.6 0 0 0 4 3.7v1.2Z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <div className="text-[9px] font-black uppercase tracking-widest text-white/60">TikTok</div>
                    <div className="font-[Outfit] text-[13px] font-black text-white leading-none truncate">@price.covoit</div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Vraies captures d'app */}
          <div className="relative flex items-center justify-center gap-4 md:justify-end md:gap-6">
            <div className="relative -mt-4">
              <PhoneFrame src="/price-site/app-client.webp" alt="App Price — vue passager" rotate={-3} delay="0s" />
              <div className="absolute -top-3 -left-3 rotate-[-6deg] rounded-2xl bg-white px-3 py-1.5 street-border-thin" style={{ boxShadow: "3px 3px 0 0 #1E0910" }}>
                <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "#F86A78" }}>Passager</div>
              </div>
            </div>
            <div className="relative mt-8 hidden md:block">
              <PhoneFrame src="/price-site/app-driver.webp" alt="App Price — vue covoitureur" rotate={3} delay="0.4s" />
              <div className="absolute -top-3 -right-3 rotate-[6deg] rounded-2xl px-3 py-1.5 street-border-thin" style={{ background: "#F4A825", boxShadow: "3px 3px 0 0 #1E0910" }}>
                <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: "#1E0910" }}>Covoitureur</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="mx-auto max-w-6xl px-5 pb-4">
        <div
          className="grid grid-cols-2 gap-3 rounded-3xl bg-white p-4 street-border-thin sm:grid-cols-4"
          style={{ boxShadow: "5px 5px 0 0 #1E0910" }}
        >
          {[
            { emoji: "📱", label: "iPhone & Android" },
            { emoji: "🔒", label: "Paiement sécurisé" },
            { emoji: "⚡", label: "Instantané" },
            { emoji: "✨", label: "Simple à prendre en main" },
          ].map((t) => (
            <div key={t.label} className="flex items-center gap-2 px-2 py-2">
              <span className="text-2xl" aria-hidden>{t.emoji}</span>
              <span className="text-[13px] font-black leading-tight" style={{ color: "#1E0910" }}>{t.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section id="comment" className="mx-auto max-w-6xl px-5 py-20">
        <div className="mb-12 text-center md:text-left">
          <div className="text-xs font-black uppercase tracking-widest" style={{ color: "#F86A78" }}>3 étapes</div>
          <h2 className="mt-2 font-[Outfit] text-4xl font-black leading-tight sm:text-5xl">Comment ça marche&nbsp;?</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={i} className="relative">
              <div className="h-full rounded-3xl p-7 street-border" style={{ background: s.bg, boxShadow: "5px 5px 0 0 #1E0910" }}>
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-4xl street-border-thin" style={{ boxShadow: "3px 3px 0 0 #1E0910" }}>
                  <span aria-hidden>{s.emoji}</span>
                </div>
                <div className="text-xs font-black uppercase tracking-widest" style={{ color: "rgba(30,9,16,0.65)" }}>Étape {i + 1}</div>
                <h3 className="mt-2 font-[Outfit] text-xl font-black leading-tight">{s.title}</h3>
                <p className="mt-3 font-[Manrope] text-sm" style={{ color: "rgba(30,9,16,0.75)" }}>{s.text}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="hidden md:flex absolute top-1/2 -right-4 z-10 h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-white street-border" style={{ background: "#1E0910", boxShadow: "3px 3px 0 0 #1E0910" }}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* POURQUOI PRICE — version CHAUDE (cream/pink/yellow, plus de noir majoritaire) */}
      <section id="pourquoi" className="relative overflow-hidden" style={{ background: "#FDD7B0" }}>
        <div className="pointer-events-none absolute -top-24 -right-20 h-80 w-80 rounded-full blur-3xl" style={{ background: "rgba(248,106,120,0.28)" }} aria-hidden />
        <div className="pointer-events-none absolute -bottom-24 -left-20 h-80 w-80 rounded-full blur-3xl" style={{ background: "rgba(244,168,37,0.30)" }} aria-hidden />

        <div className="relative mx-auto max-w-6xl px-5 py-20">
          <div className="max-w-2xl">
            <div className="text-xs font-black uppercase tracking-widest" style={{ color: "#1E0910" }}>Nos promesses</div>
            <h2 className="mt-2 font-[Outfit] text-4xl font-black leading-tight sm:text-5xl" style={{ color: "#1E0910" }}>
              Pourquoi <span style={{ color: "#F86A78" }}>Price</span>&nbsp;?
            </h2>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {perks.map((p, i) => (
              <div
                key={i}
                className="group rounded-3xl bg-white p-6 street-border-thin transition-transform hover:-translate-y-1"
                style={{ boxShadow: "5px 5px 0 0 #1E0910" }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="grid h-14 w-14 flex-none place-items-center rounded-2xl text-2xl street-border-thin"
                    style={{
                      background: ["#FFE9D6", "#B8E8D3", "#F4A825", "#F86A78"][i],
                      boxShadow: "3px 3px 0 0 #1E0910",
                    }}
                  >
                    <span aria-hidden>{p.emoji}</span>
                  </div>
                  <div>
                    <h3 className="font-[Outfit] text-lg font-black leading-tight" style={{ color: "#1E0910" }}>{p.title}</h3>
                    <p className="mt-1.5 text-sm" style={{ color: "rgba(30,9,16,0.75)" }}>{p.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ILS PARLENT DE PRICE */}
      <section id="temoignages" className="mx-auto max-w-6xl px-5 py-20">
        <div className="mb-12 text-center">
          <div className="text-xs font-black uppercase tracking-widest" style={{ color: "#F86A78" }}>Bêta-testeurs</div>
          <h2 className="mt-2 font-[Outfit] text-4xl font-black leading-tight sm:text-5xl">Ils parlent de Price.</h2>
          <p className="mt-3 font-[Manrope]" style={{ color: "rgba(30,9,16,0.6)" }}>Les retours des premiers utilisateurs.</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {testimonials.map((t, i) => (
            <div
              key={i}
              className="rounded-3xl p-6 street-border-thin"
              style={{ background: t.tint, boxShadow: "5px 5px 0 0 #1E0910" }}
            >
              <div className="text-lg" style={{ color: "#F86A78" }}>★★★★★</div>
              <p className="mt-3 font-[Outfit] text-lg font-black leading-snug" style={{ color: "#1E0910" }}>
                “{t.text}”
              </p>
              <div className="mt-4 text-xs font-black uppercase tracking-widest" style={{ color: "rgba(30,9,16,0.6)" }}>
                {t.author}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-5 py-20">
        <div className="text-center">
          <div className="text-xs font-black uppercase tracking-widest" style={{ color: "#F86A78" }}>FAQ</div>
          <h2 className="mt-2 font-[Outfit] text-4xl font-black leading-tight sm:text-5xl">Vos questions.</h2>
          <p className="mt-3 font-[Manrope]" style={{ color: "rgba(30,9,16,0.6)" }}>Tout ce qu&apos;on nous demande le plus souvent.</p>
        </div>
        <div className="mt-10 space-y-3">
          {faq.map((item, i) => (
            <details key={i} className="group rounded-3xl bg-white p-6 street-border-thin transition-shadow" style={{ boxShadow: "3px 3px 0 0 #1E0910" }}>
              <summary className="flex cursor-pointer items-center justify-between gap-6 list-none">
                <span className="font-[Outfit] text-lg font-black">{item.q}</span>
                <span className="grid h-8 w-8 flex-none place-items-center rounded-full transition-transform group-open:rotate-45" style={{ background: "#FFE9D6" }}>
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </span>
              </summary>
              <div className="mt-4 font-[Manrope] text-[15px] leading-relaxed" style={{ color: "rgba(30,9,16,0.75)" }}>{item.a}</div>
            </details>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="mx-auto max-w-6xl px-5 pb-20">
        <div className="relative overflow-hidden rounded-[36px] p-10 text-center text-white street-border md:p-16" style={{ background: "#F86A78", boxShadow: "8px 8px 0 0 #1E0910" }}>
          <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#000 1px, transparent 1px)", backgroundSize: "24px 24px" }} aria-hidden />
          <h2 className="relative font-[Outfit] text-4xl font-black leading-tight sm:text-5xl">
            Prêt à partager ton prochain trajet&nbsp;?
          </h2>
          <p className="relative mt-3 mx-auto max-w-lg opacity-90">
            Télécharge Price en 10 secondes et rejoins des milliers de covoitureurs.
          </p>
          <div className="relative mt-6 flex flex-col items-center gap-4">
            {isAndroid ? (
              <a
                href="/welcome"
                data-testid="final-android-web-cta"
                className="street-press inline-flex items-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-black uppercase tracking-widest street-border"
                style={{ color: "#F86A78", boxShadow: "8px 8px 0 0 #1E0910" }}
              >
                Ouvrir Price maintenant
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </a>
            ) : (
              <Link
                to="/download"
                data-testid="final-download-cta"
                className="street-press inline-flex items-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-black uppercase tracking-widest street-border"
                style={{ color: "#F86A78", boxShadow: "8px 8px 0 0 #1E0910" }}
              >
                Télécharger l&apos;application
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </Link>
            )}
            {isAndroid && (
              <a
                href="https://play.google.com/store/apps/details?id=com.price.covoiturage"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-android-play-bottom"
                className="text-[12px] font-bold text-white/95 underline decoration-2 underline-offset-4 hover:text-white"
              >
                Aussi disponible sur Google Play →
              </a>
            )}
          </div>
        </div>
      </section>
    </PriceSiteLayout>
  );
}
